import React, { useEffect, useState } from 'react';
import { Form, Select, Input, Switch, Collapse, Button, Spin, Typography, theme } from 'antd';
import { message } from '../../services/toastMessage';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  IconHome2,
  IconEngine,
  IconKey,
  IconCamera,
  IconChevronDown,
  IconTool,
} from '@tabler/icons-react';
import AppLayout from '../../components/Layouts/Layout';
import BackButton from '../../components/BackButton';
import LocationAutocomplete from '../../components/LocationAutocomplete';
import StaticMap from '../../components/StaticMap';
import PhotoUpload from '../../components/PhotoUpload';
import VideoUpload from '../../components/VideoUpload';
import EmojiTextArea from '../../components/EmojiTextArea';
import FloatingLabelInput from '../../components/FloatingLabelInput';
import PillNumberField from '../../components/PillNumberField';
import PillSelectField from '../../components/PillSelectField';
import WizardTileGrid from '../../components/WizardTileGrid';
import TaxonomyPicker from '../../components/TaxonomyPicker';
import OperatorAssignmentPicker from '../../components/OperatorAssignmentPicker';
import AccessoryAssignmentPicker from '../../components/AccessoryAssignmentPicker';
import InsurancePolicyAssignmentPicker from '../../components/InsurancePolicyAssignmentPicker';
import { getMyOperators, getOperatorsForMachinery } from '../../services/OperatorService';
import { IOperator } from '../../interfaces/heavyDutyMachinery/IOperator';
import { getMyMachineryAccessories, getAccessoriesForMachinery } from '../../services/MachineryAccessoryService';
import { IMachineryAccessory } from '../../interfaces/heavyDutyMachinery/IMachineryAccessory';
import { getMyHostInsurancePolicies, getInsurancePoliciesForMachinery } from '../../services/HostInsurancePolicyService';
import { IHostInsurancePolicy } from '../../interfaces/heavyDutyMachinery/IHostInsurancePolicy';
import { getMyTransportVehicles } from '../../services/TransportVehicleService';
import { ITransportVehicle } from '../../interfaces/heavyDutyMachinery/ITransportVehicle';
import type { HeavyDutyMachineryOptionIcon } from '../../constants/heavyDutyMachineryOptionIcons';
import {
  getHeavyDutyMachineryById,
  updateHeavyDutyMachinery,
} from '../../services/HeavyDutyMachineryService';
import { ICreateHeavyDutyMachineryRequest } from '../../interfaces/heavyDutyMachinery/IHeavyDutyMachinery';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import ManufacturerBrandSelect from '../../components/ManufacturerBrandSelect';
import { getMachinerySpecConfig } from '../../constants/machinerySpecsByCategory';
import { useTaxonomy } from '../../hooks/useTaxonomy';
import { motion } from 'framer-motion';
import { TEAL, TEAL_SOFT_BG, SURFACE_1_SOFT } from '../../theme/creamyTokens';
import { PROPULSION_TYPE_OPTIONS, FUEL_TYPE_OPTIONS } from '../../constants/machineryGlobalSpecOptions';
import { getCustomSpecFields } from '../../constants/machineryCustomSpecFieldsByType';
import {
  HIRE_MODE_OPTIONS,
  ACCESS_TYPE_WIZARD_OPTIONS,
  RENTAL_MODE_WIZARD_OPTIONS,
  PARKING_OPTIONS,
  CANCELLATION_POLICY_OPTIONS,
  TIMEZONE_OPTIONS,
  HeavyDutyMachineryOption,
} from '../../constants/heavyDutyMachinerySpaceOptions';

const { Text, Title } = Typography;

interface HeavyDutyMachineryFormValues extends Omit<ICreateHeavyDutyMachineryRequest, 'photos' | 'photoThumbnails' | 'videos' | 'dryHireAvailable' | 'wetHireHostOperatorAvailable' | 'wetHireThirdPartyAvailable' | 'deliveryAvailable' | 'operatorIds' | 'accessoryIds' | 'insurancePolicyIds' | 'deliveryVehicleId'> {
  photosText?: string[];
  photoThumbnailsText?: string[];
  videosList?: string[];
  hireModesList?: string[];
  // See HeavyDutyMachineryForm's identical field for the rationale --
  // collapsed into customAttributesJson at submit time, populated from it on load.
  customSpecs?: Record<string, string | number | boolean>;
  // See HeavyDutyMachineryForm's identical field -- IOperator.id strings,
  // mapped to the wire's number[] at submit time, prefilled on load from
  // getOperatorsForMachinery (the create wizard has no such prefill since a
  // brand-new machine starts with no assignments).
  operatorIds?: string[];
  // See HeavyDutyMachineryForm's identical fields -- catalog item id
  // strings, mapped to the wire's number[] at submit time, prefilled on
  // load from getAccessoriesForMachinery/getInsurancePoliciesForMachinery
  // (mapping each linked item to its own id) -- same shape those two GET
  // calls already returned for the old immediate-toggle sections this
  // replaces.
  accessoryIds?: string[];
  insurancePolicyIds?: string[];
  // See HeavyDutyMachineryForm's identical field -- an ITransportVehicle.id
  // string, mapped to the wire's number at submit time; prefilled on load
  // directly from space.deliveryVehicleId (no separate endpoint needed here,
  // unlike operatorIds -- it's a plain scalar FK, not a many-to-many link).
  deliveryVehicleId?: string;
}

type SectionKey = 'story' | 'locationSpecs' | 'accessRules' | 'extras' | 'rulesPhotos';

interface SectionConfig {
  key: SectionKey;
  titleKey: string;
  icon: HeavyDutyMachineryOptionIcon;
  // Only used to figure out which section(s) to auto-open when a field
  // fails validation -- not a validation scope like the wizard's
  // getFieldsForPhase, since this form validates everything at once.
  fields: string[];
}

// 4 sections (down from the original 7 spa-era ones) -- same restructuring
// as the wizard (HeavyDutyMachineryForm), see that file's own comment.
// 'location' + 'specs' + 'hireModes' are now one 'locationSpecs' section;
// 'ambiance' (view privacy + underwater/garden lighting) is gone entirely.
const SECTIONS: SectionConfig[] = [
  { key: 'story', titleKey: TRANSLATIONS.WIZARD_PHASE_STORY_TITLE, icon: IconHome2, fields: ['title', 'description'] },
  {
    key: 'locationSpecs',
    titleKey: TRANSLATIONS.WIZARD_PHASE_LOCATION_SPECS_TITLE,
    icon: IconEngine,
    fields: [
      'city', 'privateAddress', 'category', 'manufacturerBrand', 'modelName', 'yearManufactured', 'engineHoursAtListing',
      'serialNumber', 'internalReference',
      'weightTons', 'powerHp', 'reachMeters', 'hireModesList', 'overtimeHourRate', 'depositAmount',
    ],
  },
  {
    key: 'accessRules',
    titleKey: TRANSLATIONS.WIZARD_PHASE_ACCESS_RULES_TITLE,
    icon: IconKey,
    fields: [
      'accessType', 'rentalMode', 'pricePerHour', 'pricePerNight', 'maxOperators', 'parking',
      'bufferMinutes', 'minAdvanceBookingHours', 'maxAdvanceBookingDays', 'minBookingDurationMinutes', 'maxBookingDurationMinutes', 'slotGranularityMinutes',
    ],
  },
  {
    key: 'extras',
    titleKey: TRANSLATIONS.WIZARD_PHASE_EXTRAS_TITLE,
    icon: IconTool,
    fields: ['accessoryIds', 'insurancePolicyIds'],
  },
  {
    key: 'rulesPhotos',
    titleKey: TRANSLATIONS.WIZARD_PHASE_RULES_PHOTOS_TITLE,
    icon: IconCamera,
    fields: ['cancellationPolicy', 'timezone', 'welcomeMessage', 'photosText', 'videosList'],
  },
];

const FieldGroup: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 28 }}>
    {label && (
      <Text strong style={{ display: 'block', fontSize: 15, marginBottom: 12 }}>
        {label}
      </Text>
    )}
    {children}
  </div>
);

/**
 * "Editar espacio" for an already-published listing -- everything the
 * create wizard (HeavyDutyMachineryForm) asks for, same field groupings, but as
 * a single page of collapsible sections instead of a paged narrative: no
 * reason to walk through 7 steps just to fix one field. The wizard stays
 * exclusively for "publish a new space" (see its own doc comment); this
 * page is only ever reached via /heavy-duty-machinery/:id/edit.
 */
const HeavyDutyMachineryEditForm: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm<HeavyDutyMachineryFormValues>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [openKeys, setOpenKeys] = useState<SectionKey[]>(SECTIONS.map((s) => s.key));
  const [errorSections, setErrorSections] = useState<Set<SectionKey>>(new Set());

  // Same reasoning as the wizard's own copy of this: rentalMode gates
  // fields across two different sections, so it's tracked from the Form
  // instance's own onValuesChange rather than Form.useWatch.
  const [rentalMode, setRentalMode] = useState<string | undefined>(undefined);
  // Same reasoning: maxOperators only makes sense once some wet-hire mode is
  // on, tracked the same way as rentalMode above.
  const [hasWetHire, setHasWetHire] = useState(false);
  // Same reasoning as hasWetHire above -- gates the new delivery fields
  // block below.
  const [hasDelivery, setHasDelivery] = useState(false);
  const [operators, setOperators] = useState<IOperator[]>([]);
  const [operatorsLoading, setOperatorsLoading] = useState(true);
  const [accessories, setAccessories] = useState<IMachineryAccessory[]>([]);
  const [accessoriesLoading, setAccessoriesLoading] = useState(true);
  const [insurancePolicies, setInsurancePolicies] = useState<IHostInsurancePolicy[]>([]);
  const [insurancePoliciesLoading, setInsurancePoliciesLoading] = useState(true);
  const [transportVehicles, setTransportVehicles] = useState<ITransportVehicle[]>([]);
  const selectedCountryName = Form.useWatch('country', form);
  const selectedLatitude = Form.useWatch('latitude', form);
  const selectedLongitude = Form.useWatch('longitude', form);
  const photos = Form.useWatch('photosText', form);
  const photoThumbnails = Form.useWatch('photoThumbnailsText', form);
  const videos = Form.useWatch('videosList', form);
  const { isLightEquipment } = useTaxonomy();
  const selectedCategory = Form.useWatch('category', form);
  const isLight = isLightEquipment(selectedCategory);
  /**
   * Light equipment hides the heavy specs in the publish wizard, and this is
   * the way back to them. Only here, on edit, and never on the wizard: the
   * point of the short form is that somebody listing a lawnmower is not made
   * to walk past questions meant for a 20-tonne machine. But once the
   * listing exists, hiding those fields for good would trap whoever does
   * want to fill one in -- a mini-digger sold as garden equipment, a
   * generator whose transport height genuinely matters.
   *
   * Closed by default, and no field it holds is ever required: opening it is
   * an offer, not a chore.
   */
  const [showAdvancedSpecs, setShowAdvancedSpecs] = useState(false);
  const specConfig = getMachinerySpecConfig(selectedCategory, isLight && !showAdvancedSpecs);
  const customSpecFields = getCustomSpecFields(selectedCategory);

  const requiredRule = { required: true, message: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FIELD_REQUIRED) };
  const opts = (options: HeavyDutyMachineryOption[]) => options.map((o) => ({ label: translate(t, o.labelKey), value: o.value }));

  useEffect(() => {
    if (!id) {
      navigate('/heavy-duty-machinery/mine', { replace: true });
      return;
    }
    Promise.all([
      getHeavyDutyMachineryById(id),
      getMyOperators(),
      getOperatorsForMachinery(id),
      getMyMachineryAccessories(),
      getAccessoriesForMachinery(id),
      getMyHostInsurancePolicies(),
      getInsurancePoliciesForMachinery(id),
      getMyTransportVehicles(),
    ])
      .then(([space, myOperators, assignedOperatorIds, myAccessories, linkedAccessories, myInsurancePolicies, linkedInsurancePolicies, myTransportVehicles]) => {
        setOperators(myOperators);
        setOperatorsLoading(false);
        setAccessories(myAccessories);
        setAccessoriesLoading(false);
        setInsurancePolicies(myInsurancePolicies);
        setInsurancePoliciesLoading(false);
        setTransportVehicles(myTransportVehicles);
        if (!space) {
          message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_NOT_FOUND));
          navigate('/heavy-duty-machinery/mine');
          return;
        }
        const hireModesList = [
          space.dryHireAvailable && 'DryHireAvailable',
          space.wetHireHostOperatorAvailable && 'WetHireHostOperatorAvailable',
          space.wetHireThirdPartyAvailable && 'WetHireThirdPartyAvailable',
          space.deliveryAvailable && 'DeliveryAvailable',
        ].filter((v): v is string => !!v);
        let customSpecs: Record<string, string | number | boolean> | undefined;
        if (space.customAttributesJson) {
          try {
            customSpecs = JSON.parse(space.customAttributesJson);
          } catch {
            customSpecs = undefined;
          }
        }
        form.setFieldsValue({
          ...space,
          photosText: space.photos ?? [],
          photoThumbnailsText: space.photoThumbnails ?? [],
          videosList: space.videos ?? [],
          hireModesList,
          customSpecs,
          operatorIds: assignedOperatorIds,
          accessoryIds: linkedAccessories.map((l) => l.id),
          insurancePolicyIds: linkedInsurancePolicies.map((l) => l.id),
        });
        // setFieldsValue doesn't fire onValuesChange -- the one place that
        // otherwise keeps rentalMode/hasWetHire's state copies in sync (see
        // their own declarations above).
        setRentalMode(space.rentalMode);
        setHasWetHire(space.wetHireHostOperatorAvailable || space.wetHireThirdPartyAvailable);
        setHasDelivery(space.deliveryAvailable);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSave = async () => {
    setErrorSections(new Set());
    try {
      await form.validateFields();
    } catch (err: any) {
      const invalidNames: string[] = (err?.errorFields ?? []).map((f: { name: (string | number)[] }) => String(f.name[0]));
      const sectionsToOpen = SECTIONS.filter((s) => s.fields.some((f) => invalidNames.includes(f))).map((s) => s.key);
      setOpenKeys((prev) => Array.from(new Set([...prev, ...sectionsToOpen])));
      setErrorSections(new Set(sectionsToOpen));
      // Give the just-opened section(s) a beat to expand before scrolling --
      // their fields are already mounted (antd Collapse doesn't unmount
      // inactive panels), just visually collapsed, so this only needs to
      // wait for the open transition rather than for anything to mount.
      const firstInvalid = invalidNames[0];
      if (firstInvalid) {
        setTimeout(() => form.scrollToField(firstInvalid as any, { behavior: 'smooth', block: 'center' }), 80);
      }
      return;
    }

    const values = form.getFieldsValue(true) as HeavyDutyMachineryFormValues;
    const { photosText, photoThumbnailsText, videosList, hireModesList, customSpecs, operatorIds, accessoryIds, insurancePolicyIds, deliveryVehicleId, ...rest } = values;

    const hireModes = new Set(hireModesList ?? []);

    const payload = {
      ...rest,
      photos: photosText,
      photoThumbnails: photoThumbnailsText,
      videos: videosList,
      dryHireAvailable: hireModes.has('DryHireAvailable'),
      wetHireHostOperatorAvailable: hireModes.has('WetHireHostOperatorAvailable'),
      wetHireThirdPartyAvailable: hireModes.has('WetHireThirdPartyAvailable'),
      deliveryAvailable: hireModes.has('DeliveryAvailable'),
      customAttributesJson: customSpecs && Object.keys(customSpecs).length > 0 ? JSON.stringify(customSpecs) : undefined,
      // Full replacement -- see UpdateHeavyDutyMachineryRequest.OperatorIds's
      // own doc comment (API repo). Always sent (never undefined) here,
      // unlike the wizard's own conversion: this section is always mounted
      // (Collapse doesn't unmount inactive panels) so operatorIds is always
      // a real array, even an empty one when the host unassigns everyone.
      operatorIds: (operatorIds ?? []).map(Number),
      // Same "always sent, full replacement" reasoning as operatorIds above.
      accessoryIds: (accessoryIds ?? []).map(Number),
      insurancePolicyIds: (insurancePolicyIds ?? []).map(Number),
      deliveryVehicleId: deliveryVehicleId ? Number(deliveryVehicleId) : undefined,
    };

    setSubmitting(true);
    try {
      await updateHeavyDutyMachinery(id!, payload);
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_SAVED_MESSAGE));
      navigate('/heavy-duty-machinery/mine');
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_SAVE_ERROR));
    } finally {
      setSubmitting(false);
    }
  };

  const renderSectionErrorDot = (key: SectionKey) =>
    errorSections.has(key) && (
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: '#C24A3B',
          flexShrink: 0,
        }}
      />
    );

  const renderSectionFields = (key: SectionKey) => {
    switch (key) {
      case 'story':
        return (
          <>
            <FieldGroup label="">
              <Form.Item name="title" rules={[requiredRule]} style={{ marginBottom: 20 }}>
                <FloatingLabelInput label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_TITLE_LABEL)} maxLength={200} disabled={submitting} />
              </Form.Item>
            </FieldGroup>
            <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_DESCRIPTION_LABEL)}>
              <Form.Item name="description" rules={[requiredRule]}>
                <EmojiTextArea rows={5} maxLength={4000} disabled={submitting} />
              </Form.Item>
            </FieldGroup>
          </>
        );

      case 'locationSpecs':
        return (
          <>
            <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_LOCATION_LABEL)}>
              <Form.Item
                name="city"
                rules={[
                  requiredRule,
                  {
                    validator: () =>
                      form.getFieldValue('countryCode')
                        ? Promise.resolve()
                        : Promise.reject(new Error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_LOCATION_NOT_SELECTED))),
                  },
                ]}
              >
                <LocationAutocomplete
                  disabled={submitting}
                  includeCountryInLabel
                  placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_LOCATION_PLACEHOLDER)}
                  onChange={() => form.setFieldsValue({ countryCode: undefined })}
                  onSelectLocation={(location) => {
                    form.setFieldsValue({
                      province: location.province,
                      region: location.region,
                      country: location.country,
                      countryCode: location.countryCode,
                      formattedAddress: location.formattedAddress,
                      administrativeLevel: location.administrativeLevel,
                      latitude: location.latitude,
                      longitude: location.longitude,
                    });
                  }}
                />
              </Form.Item>
              {selectedCountryName && (
                <Text type="secondary" style={{ display: 'block', marginTop: -8, marginBottom: 8 }}>
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_COUNTRY_LABEL)}: {selectedCountryName}
                </Text>
              )}
              {selectedLatitude != null && selectedLongitude != null && (
                <div style={{ marginTop: 8 }}>
                  <StaticMap latitude={selectedLatitude} longitude={selectedLongitude} />
                </div>
              )}
              <Form.Item name="province" hidden><input /></Form.Item>
              <Form.Item name="region" hidden><input /></Form.Item>
              <Form.Item name="country" hidden><input /></Form.Item>
              <Form.Item name="countryCode" hidden><input /></Form.Item>
              <Form.Item name="formattedAddress" hidden><input /></Form.Item>
              <Form.Item name="administrativeLevel" hidden><input /></Form.Item>
              <Form.Item name="latitude" hidden><input /></Form.Item>
              <Form.Item name="longitude" hidden><input /></Form.Item>
            </FieldGroup>

            <FieldGroup label="">
              <Form.Item name="privateAddress" style={{ marginBottom: 4 }}>
                <FloatingLabelInput label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRIVATE_ADDRESS_LABEL)} disabled={submitting} />
              </Form.Item>
              <Text type="secondary" style={{ fontSize: 12 }}>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRIVATE_ADDRESS_HELP)}</Text>
            </FieldGroup>

            <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CATEGORY_LABEL)}>
              <Form.Item name="category" rules={[requiredRule]}>
                <TaxonomyPicker disabled={submitting} />
              </Form.Item>
            </FieldGroup>

            <FieldGroup label="">
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <Form.Item name="manufacturerBrand" style={{ flex: 1, minWidth: 180 }}>
                  <ManufacturerBrandSelect
                    label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MANUFACTURER_BRAND_LABEL)}
                    disabled={submitting}
                  />
                </Form.Item>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MODEL_NAME_LABEL)}
                  </Text>
                  <Form.Item name="modelName" style={{ marginBottom: 0 }}>
                    <Input disabled={submitting} />
                  </Form.Item>
                </div>
              </div>
            </FieldGroup>

            <FieldGroup label="">
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <Form.Item name="yearManufactured" style={{ flex: 1, minWidth: 180 }}>
                  <PillNumberField label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_YEAR_MANUFACTURED_LABEL)} min={1950} disabled={submitting} />
                </Form.Item>
                {specConfig.engineHoursAtListing !== 'hidden' && (
                  <Form.Item name="engineHoursAtListing" style={{ flex: 1, minWidth: 180 }}>
                    <PillNumberField label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_ENGINE_HOURS_LABEL)} min={0} disabled={submitting} />
                  </Form.Item>
                )}
              </div>
            </FieldGroup>

            {/* Una píldora, no un acordeón de antd: el resto de esta
                pantalla habla en píldoras y superficies creamy, y un panel
                gris del framework aquí se leería como de otra aplicación.
                Sólo aparece para equipo ligero -- en una excavadora estos
                campos nunca estuvieron escondidos, así que no hay nada que
                desplegar. */}
            {isLight && (
              <button
                type="button"
                onClick={() => setShowAdvancedSpecs((open) => !open)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  alignSelf: 'flex-start',
                  marginBottom: 20,
                  padding: '8px 16px',
                  borderRadius: 999,
                  border: `1px solid ${showAdvancedSpecs ? TEAL : token.colorBorderSecondary}`,
                  background: showAdvancedSpecs ? TEAL_SOFT_BG : SURFACE_1_SOFT,
                  color: showAdvancedSpecs ? TEAL : token.colorTextSecondary,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
                }}
              >
                <motion.span
                  animate={{ rotate: showAdvancedSpecs ? 180 : 0 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                  style={{ display: 'flex' }}
                >
                  <IconChevronDown size={15} />
                </motion.span>
                {translate(
                  t,
                  showAdvancedSpecs
                    ? TRANSLATIONS.MACHINERY_ADVANCED_SPECS_HIDE
                    : TRANSLATIONS.MACHINERY_ADVANCED_SPECS_SHOW
                )}
              </button>
            )}

            {/* Ocultos para equipo ligero salvo que el host los pida
                (ver showAdvancedSpecs). */}
            {!(isLight && !showAdvancedSpecs) && (
              <FieldGroup label="">
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <Form.Item
                    name="weightTons"
                    rules={specConfig.weightTons === 'required' ? [requiredRule] : undefined}
                    style={{ flex: 1, minWidth: 180 }}
                  >
                    <PillNumberField label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_WEIGHT_TONS_LABEL)} min={0} step={0.1} disabled={submitting} />
                  </Form.Item>
                  <Form.Item
                    name="powerHp"
                    rules={specConfig.powerHp === 'required' ? [requiredRule] : undefined}
                    style={{ flex: 1, minWidth: 180 }}
                  >
                    <PillNumberField label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_POWER_HP_LABEL)} min={0} disabled={submitting} />
                  </Form.Item>
                  {specConfig.reachMeters !== 'hidden' && (
                    <Form.Item
                      name="reachMeters"
                      rules={specConfig.reachMeters === 'required' ? [requiredRule] : undefined}
                      style={{ flex: 1, minWidth: 180 }}
                    >
                      <PillNumberField label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_REACH_METERS_LABEL)} min={0} step={0.1} disabled={submitting} />
                    </Form.Item>
                  )}
                </div>
              </FieldGroup>
            )}

            {!(isLight && !showAdvancedSpecs) && (
              <FieldGroup label={translate(t, TRANSLATIONS.MACHINERY_SPEC_SECTION_TITLE)}>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 260 }}>
                    <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
                      {translate(t, TRANSLATIONS.MACHINERY_SPEC_PROPULSION_TYPE_LABEL)}
                    </Text>
                    <Form.Item name="propulsionType" style={{ marginBottom: 0 }}>
                      <WizardTileGrid options={PROPULSION_TYPE_OPTIONS} disabled={submitting} minTileWidth={100} />
                    </Form.Item>
                  </div>
                  <div style={{ flex: 1, minWidth: 260 }}>
                    <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
                      {translate(t, TRANSLATIONS.MACHINERY_SPEC_FUEL_TYPE_LABEL)}
                    </Text>
                    <Form.Item name="fuelType" style={{ marginBottom: 0 }}>
                      <WizardTileGrid options={FUEL_TYPE_OPTIONS} disabled={submitting} minTileWidth={100} />
                    </Form.Item>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 16 }}>
                  <Form.Item name="transportLengthM" style={{ flex: 1, minWidth: 180 }}>
                    <PillNumberField label={translate(t, TRANSLATIONS.MACHINERY_SPEC_TRANSPORT_LENGTH_LABEL)} min={0} step={0.1} disabled={submitting} />
                  </Form.Item>
                  <Form.Item name="transportWidthM" style={{ flex: 1, minWidth: 180 }}>
                    <PillNumberField label={translate(t, TRANSLATIONS.MACHINERY_SPEC_TRANSPORT_WIDTH_LABEL)} min={0} step={0.1} disabled={submitting} />
                  </Form.Item>
                  <Form.Item name="transportHeightM" style={{ flex: 1, minWidth: 180 }}>
                    <PillNumberField label={translate(t, TRANSLATIONS.MACHINERY_SPEC_TRANSPORT_HEIGHT_LABEL)} min={0} step={0.1} disabled={submitting} />
                  </Form.Item>
                </div>
                <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Form.Item name="hasQuickHitch" valuePropName="checked" style={{ marginBottom: 0 }}>
                    <Switch disabled={submitting} />
                  </Form.Item>
                  <Text>{translate(t, TRANSLATIONS.MACHINERY_SPEC_HAS_QUICK_HITCH_LABEL)}</Text>
                </div>
              </FieldGroup>
            )}

            {customSpecFields.length > 0 && (
              <div style={{ marginBottom: 28 }}>
                <Collapse
                  ghost
                  className="heavyDutyMachinery-optional-collapse"
                  expandIconPosition="end"
                  expandIcon={({ isActive }) => (
                    <IconChevronDown
                      size={18}
                      color={token.colorTextSecondary}
                      style={{ transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 200ms ease' }}
                    />
                  )}
                  items={[
                    {
                      key: 'customSpecs',
                      label: (
                        <Text strong style={{ fontSize: 15 }}>
                          {translate(t, TRANSLATIONS.MACHINERY_SPEC_CUSTOM_SECTION_TITLE)}
                        </Text>
                      ),
                      children: (
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                          {customSpecFields.map((field) => {
                    const fieldLabel = `${translate(t, field.labelKey)}${field.unitKey ? ` (${translate(t, field.unitKey)})` : ''}`;
                    if (field.type === 'boolean') {
                      return (
                        <div key={field.key} style={{ flex: 1, minWidth: 180, display: 'flex', alignItems: 'center', gap: 12 }}>
                          <Form.Item name={['customSpecs', field.key]} valuePropName="checked" style={{ marginBottom: 0 }}>
                            <Switch disabled={submitting} />
                          </Form.Item>
                          <Text>{fieldLabel}</Text>
                        </div>
                      );
                    }
                    if (field.type === 'select') {
                      return (
                        <div key={field.key} style={{ flex: 1, minWidth: 180 }}>
                          <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
                            {fieldLabel}
                          </Text>
                          <Form.Item name={['customSpecs', field.key]} style={{ marginBottom: 0 }}>
                            <Select
                              allowClear
                              disabled={submitting}
                              options={(field.options ?? []).map((option) => ({ value: option.value, label: translate(t, option.labelKey) }))}
                            />
                          </Form.Item>
                        </div>
                      );
                    }
                    if (field.type === 'number') {
                      return (
                        <Form.Item key={field.key} name={['customSpecs', field.key]} style={{ flex: 1, minWidth: 180 }}>
                          <PillNumberField label={fieldLabel} min={0} step={0.1} disabled={submitting} />
                        </Form.Item>
                      );
                    }
                    return (
                      <div key={field.key} style={{ flex: 1, minWidth: 180 }}>
                        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
                          {fieldLabel}
                        </Text>
                        <Form.Item name={['customSpecs', field.key]} style={{ marginBottom: 0 }}>
                          <Input disabled={submitting} />
                        </Form.Item>
                      </div>
                    );
                          })}
                        </div>
                      ),
                    },
                  ]}
                />
              </div>
            )}

            <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_RENTAL_AVAILABILITY_LABEL)}>
              <Form.Item name="hireModesList">
                <WizardTileGrid options={HIRE_MODE_OPTIONS} multiple disabled={submitting} />
              </Form.Item>
            </FieldGroup>

            {/* Reserva diferida -- a different kind of setting than the
                hire-modes checklist above, its own plain boolean rather than
                folded into hireModesList's own cross-mapping. */}
            <FieldGroup label="">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Form.Item name="deferredBookingEnabled" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <Switch disabled={submitting} />
                </Form.Item>
                <Text>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_DEFERRED_BOOKING_LABEL)}</Text>
              </div>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_DEFERRED_BOOKING_HELP)}
              </Text>
            </FieldGroup>

            <FieldGroup label="">
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <Form.Item name="overtimeHourRate" style={{ flex: 1, minWidth: 180 }}>
                  <PillNumberField
                    label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_OVERTIME_HOUR_RATE_LABEL)}
                    min={0}
                    step={0.01}
                    addonAfter="€"
                    disabled={submitting}
                  />
                </Form.Item>
                <Form.Item name="depositAmount" style={{ flex: 1, minWidth: 180 }}>
                  <PillNumberField
                    label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_DEPOSIT_AMOUNT_LABEL)}
                    min={0}
                    step={0.01}
                    addonAfter="€"
                    disabled={submitting}
                  />
                </Form.Item>
              </div>
            </FieldGroup>
          </>
        );

      case 'accessRules':
        return (
          <>
            <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_ACCESS_TYPE_LABEL)}>
              <Form.Item name="accessType" rules={[requiredRule]}>
                <WizardTileGrid options={ACCESS_TYPE_WIZARD_OPTIONS} disabled={submitting} />
              </Form.Item>
            </FieldGroup>

            <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_RENTAL_MODE_LABEL)}>
              <Form.Item name="rentalMode" rules={[requiredRule]}>
                <WizardTileGrid options={RENTAL_MODE_WIZARD_OPTIONS} disabled={submitting} />
              </Form.Item>
            </FieldGroup>

            {hasWetHire && (
              <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_ASSIGNMENT_LABEL)}>
                <Form.Item name="operatorIds" style={{ marginBottom: 4 }}>
                  <OperatorAssignmentPicker
                    operators={operators}
                    loading={operatorsLoading}
                    qualifiedCategory={selectedCategory}
                    disabled={submitting}
                  />
                </Form.Item>
                <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATOR_ASSIGNMENT_HELP)}
                </Text>
              </FieldGroup>
            )}

            {hasDelivery && (
              <>
                <FieldGroup label="">
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <Form.Item name="pricePerKmDelivery" style={{ flex: 1, minWidth: 180 }}>
                      <PillNumberField
                        label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRICE_PER_KM_DELIVERY_LABEL)}
                        min={0}
                        step={0.01}
                        addonAfter="€"
                        disabled={submitting}
                      />
                    </Form.Item>
                    <Form.Item name="fixedDispatchFee" style={{ flex: 1, minWidth: 180 }}>
                      <PillNumberField
                        label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_FIXED_DISPATCH_FEE_LABEL)}
                        min={0}
                        step={0.01}
                        addonAfter="€"
                        disabled={submitting}
                      />
                    </Form.Item>
                  </div>
                </FieldGroup>

                <FieldGroup label="">
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <Form.Item name="deliveryVehicleId" style={{ flex: 1, minWidth: 180 }}>
                      <PillSelectField
                        label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_DELIVERY_VEHICLE_LABEL)}
                        options={transportVehicles.filter((v) => v.isActive).map((v) => ({ value: v.id, label: v.name }))}
                        disabled={submitting}
                      />
                    </Form.Item>
                  </div>
                  {transportVehicles.length === 0 ? (
                    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                      <Link to="/heavy-duty-machinery/transport-vehicles" target="_blank" rel="noopener noreferrer">
                        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_DELIVERY_VEHICLE_EMPTY_CTA)}
                      </Link>
                    </Text>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_DELIVERY_VEHICLE_HELP)}
                    </Text>
                  )}
                </FieldGroup>

                <FieldGroup label="">
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <Form.Item name="maxDeliveryRadiusKm" style={{ flex: 1, minWidth: 180 }}>
                      <PillNumberField
                        label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MAX_DELIVERY_RADIUS_KM_LABEL)}
                        min={0}
                        step={1}
                        addonAfter="km"
                        disabled={submitting}
                      />
                    </Form.Item>
                    <Form.Item name="minRentalDaysForDelivery" style={{ flex: 1, minWidth: 180 }}>
                      <PillNumberField
                        label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MIN_RENTAL_DAYS_FOR_DELIVERY_LABEL)}
                        min={1}
                        step={1}
                        disabled={submitting}
                      />
                    </Form.Item>
                  </div>
                </FieldGroup>

                <FieldGroup label="">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Form.Item name="restrictDeliveryToSameCountry" valuePropName="checked" style={{ marginBottom: 0 }}>
                      <Switch disabled={submitting} />
                    </Form.Item>
                    <Text>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_RESTRICT_DELIVERY_TO_SAME_COUNTRY_LABEL)}</Text>
                  </div>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_RESTRICT_DELIVERY_TO_SAME_COUNTRY_HELP)}
                  </Text>
                </FieldGroup>

                <FieldGroup label="">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Form.Item name="chargeStaffTimeForDelivery" valuePropName="checked" style={{ marginBottom: 0 }}>
                      <Switch disabled={submitting} />
                    </Form.Item>
                    <Text>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CHARGE_STAFF_TIME_FOR_DELIVERY_LABEL)}</Text>
                  </div>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CHARGE_STAFF_TIME_FOR_DELIVERY_HELP)}
                  </Text>
                </FieldGroup>
              </>
            )}

            <FieldGroup label="">
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {(rentalMode === 'Hourly' || rentalMode === 'Both') && (
                  <Form.Item name="pricePerHour" rules={[requiredRule]} style={{ flex: 1, minWidth: 180 }}>
                    <PillNumberField
                      label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRICE_PER_HOUR_LABEL)}
                      min={0.01}
                      step={0.01}
                      addonAfter="€"
                      disabled={submitting}
                    />
                  </Form.Item>
                )}
                {(rentalMode === 'Overnight' || rentalMode === 'Both') && (
                  <Form.Item name="pricePerNight" rules={[requiredRule]} style={{ flex: 1, minWidth: 180 }}>
                    <PillNumberField
                      label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRICE_PER_NIGHT_LABEL)}
                      min={0.01}
                      step={0.01}
                      addonAfter="€"
                      disabled={submitting}
                    />
                  </Form.Item>
                )}
                {hasWetHire && (
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <Form.Item name="maxOperators" rules={[requiredRule]} style={{ marginBottom: 4 }}>
                      <PillNumberField label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MAX_OPERATORS_LABEL)} min={1} disabled={submitting} />
                    </Form.Item>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MAX_OPERATORS_HELP)}
                    </Text>
                  </div>
                )}
              </div>
            </FieldGroup>

            {(rentalMode === 'Overnight' || rentalMode === 'Both') && (
              <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_DAILY_SCHEDULE_GROUP_LABEL)}>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <Form.Item name="maxAdvanceBookingDays" rules={[requiredRule]} style={{ flex: 1, minWidth: 180 }}>
                    <PillNumberField label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MAX_ADVANCE_BOOKING_DAYS_LABEL)} min={1} disabled={submitting} />
                  </Form.Item>
                </div>
              </FieldGroup>
            )}

            {(rentalMode === 'Hourly' || rentalMode === 'Both') && (
              <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_HOURLY_SCHEDULE_GROUP_LABEL)}>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <Form.Item name="bufferMinutes" rules={[requiredRule]} style={{ flex: 1, minWidth: 180 }}>
                    <PillNumberField label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_BUFFER_MINUTES_LABEL)} min={0} disabled={submitting} />
                  </Form.Item>
                  <Form.Item name="minAdvanceBookingHours" rules={[requiredRule]} style={{ flex: 1, minWidth: 180 }}>
                    <PillNumberField label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MIN_ADVANCE_BOOKING_HOURS_LABEL)} min={0} disabled={submitting} />
                  </Form.Item>
                  <Form.Item name="minBookingDurationMinutes" rules={[requiredRule]} style={{ flex: 1, minWidth: 180 }}>
                    <PillNumberField label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MIN_BOOKING_DURATION_LABEL)} min={1} disabled={submitting} />
                  </Form.Item>
                  <Form.Item name="maxBookingDurationMinutes" rules={[requiredRule]} style={{ flex: 1, minWidth: 180 }}>
                    <PillNumberField label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MAX_BOOKING_DURATION_LABEL)} min={1} disabled={submitting} />
                  </Form.Item>
                  <Form.Item name="slotGranularityMinutes" rules={[requiredRule]} style={{ flex: 1, minWidth: 180 }}>
                    <PillNumberField label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_SLOT_GRANULARITY_LABEL)} min={0} disabled={submitting} />
                  </Form.Item>
                </div>
              </FieldGroup>
            )}

            <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PARKING_LABEL)}>
              <Form.Item name="parking" rules={[requiredRule]}>
                <WizardTileGrid options={PARKING_OPTIONS} disabled={submitting} />
              </Form.Item>
            </FieldGroup>
          </>
        );

      case 'extras':
        return (
          <>
            <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_LINK_SECTION_TITLE)}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_LINK_SECTION_SUBTITLE)}
              </Text>
              <Form.Item name="accessoryIds" style={{ marginBottom: 0 }}>
                <AccessoryAssignmentPicker
                  accessories={accessories}
                  loading={accessoriesLoading}
                  category={selectedCategory}
                  disabled={submitting}
                />
              </Form.Item>
            </FieldGroup>

            <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_LINK_SECTION_TITLE)}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INSURANCE_POLICY_LINK_SECTION_SUBTITLE)}
              </Text>
              <Form.Item name="insurancePolicyIds" style={{ marginBottom: 0 }}>
                <InsurancePolicyAssignmentPicker
                  policies={insurancePolicies}
                  loading={insurancePoliciesLoading}
                  disabled={submitting}
                />
              </Form.Item>
            </FieldGroup>
          </>
        );

      case 'rulesPhotos':
        return (
          <>
            <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CANCELLATION_POLICY_LABEL)}>
              <Form.Item name="cancellationPolicy" rules={[requiredRule]}>
                <WizardTileGrid options={CANCELLATION_POLICY_OPTIONS} disabled={submitting} />
              </Form.Item>
            </FieldGroup>

            <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_TIMEZONE_LABEL)}>
              <Form.Item name="timezone" rules={[requiredRule]}>
                <Select options={opts(TIMEZONE_OPTIONS)} disabled={submitting} />
              </Form.Item>
            </FieldGroup>

            <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_WELCOME_MESSAGE_LABEL)}>
              <Form.Item name="welcomeMessage" style={{ marginBottom: 4 }}>
                <EmojiTextArea
                  rows={4}
                  maxLength={2000}
                  disabled={submitting}
                  placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_WELCOME_MESSAGE_PLACEHOLDER)}
                />
              </Form.Item>
              <Text type="secondary" style={{ fontSize: 12 }}>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_WELCOME_MESSAGE_HELP)}</Text>
            </FieldGroup>

            <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PHOTOS_LABEL)}>
              <PhotoUpload
                domain="spaces"
                value={photos}
                thumbnails={photoThumbnails}
                onChange={(newPhotos, newThumbnails) =>
                  form.setFieldsValue({ photosText: newPhotos, photoThumbnailsText: newThumbnails })
                }
                disabled={submitting}
              />
              <Form.Item name="photosText" hidden><input /></Form.Item>
              <Form.Item name="photoThumbnailsText" hidden><input /></Form.Item>
            </FieldGroup>

            <FieldGroup label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_VIDEOS_LABEL)}>
              <VideoUpload
                domain="spaces"
                value={videos}
                onChange={(newVideos) => form.setFieldsValue({ videosList: newVideos })}
                disabled={submitting}
              />
              <Form.Item name="videosList" hidden><input /></Form.Item>
            </FieldGroup>
          </>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div style={{ width: '100%', maxWidth: 820, paddingBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          <BackButton fallbackPath="/heavy-duty-machinery/mine" />
          <Title level={3} style={{ margin: 0 }}>
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_EDIT_TITLE)}
          </Title>
        </div>

        <Form
          form={form}
          layout="vertical"
          component={false}
          initialValues={{
            maxOperators: 2,
            bufferMinutes: 30,
            minAdvanceBookingHours: 2,
            maxAdvanceBookingDays: 60,
            slotGranularityMinutes: 30,
            cancellationPolicy: 'Moderate',
            timezone: 'Europe/Madrid',
          }}
          onValuesChange={(changedValues) => {
            if ('rentalMode' in changedValues) setRentalMode(changedValues.rentalMode);
            if ('hireModesList' in changedValues) {
              const list: string[] = changedValues.hireModesList ?? [];
              setHasWetHire(list.includes('WetHireHostOperatorAvailable') || list.includes('WetHireThirdPartyAvailable'));
              setHasDelivery(list.includes('DeliveryAvailable'));
            }
          }}
        >
          <Collapse
            className="heavyDutyMachinery-edit-accordion"
            activeKey={openKeys}
            onChange={(keys) => setOpenKeys(keys as SectionKey[])}
            expandIconPosition="end"
            expandIcon={({ isActive }) => (
              <IconChevronDown
                size={18}
                color={token.colorTextSecondary}
                style={{ transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 200ms ease' }}
              />
            )}
            items={SECTIONS.map((section) => ({
              key: section.key,
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <section.icon size={18} color={token.colorTextSecondary} />
                  <Text strong style={{ fontSize: 15 }}>
                    {translate(t, section.titleKey)}
                  </Text>
                  {renderSectionErrorDot(section.key)}
                </div>
              ),
              children: renderSectionFields(section.key),
            }))}
          />
        </Form>

        <div
          style={{
            position: 'sticky',
            bottom: 0,
            marginTop: 20,
            padding: '16px 0',
            background: token.colorBgLayout,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button shape="round" size="large" type="primary" onClick={handleSave} loading={submitting}>
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_SAVE_CHANGES_BUTTON)}
          </Button>
        </div>
      </div>
    </AppLayout>
  );
};

export default HeavyDutyMachineryEditForm;
